/**
 * Flatten object and keep property naming, usable for converting nested
 * object into single level objects
 * @example:
 * const obj = {
 *      stringProperty: 'my first level text'
 *      objProperty: {
 *          text: 'my second level text'
 *      }
 * }
 *
 * flattenObjectProperties(obj, false, 'text');
 * Output:
 * {
 *      stringProperty: 'my first level text',
 *      objProperty: 'my second level text'
 * }
 * @param obj
 * @param deep
 * @param properties
 */
export const flattenObjectProperties = (
    obj: Record<string, any>,
    deep: boolean,
    ...properties: string[]
): Record<string, string> =>
    Object.keys(obj).reduce((prev: Record<string, string>, curr: string) => {
        const property = obj[curr];
        let hit = null;

        if (typeof property === 'string') {
            hit = property;
        }

        if (!Array.isArray(property) && typeof property === 'object') {
            const propToAdd = Object.entries(property).find(([key, _]) => properties.some(prop => prop === key));

            if (propToAdd) {
                hit = propToAdd[1] as string;
            }

            if (!propToAdd && deep) {
                return flattenObjectProperties({ ...prev, ...property }, true, ...properties);
            }
        }

        if (hit) {
            prev[curr] = hit;
        }

        return prev;
    }, {});
